import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactECharts from "echarts-for-react";
import { Button, Group, Layout, FormControl, Tooltip } from "@/comn/components";
import { TFormSchema, useForm, useResource, useToast } from "@/comn/hooks";
import { comnUtils } from "@/comn/utils";
import { api } from "@/comn";
import classNames from "classnames";
import dayjs from "dayjs";

type Chart = {
    trgtRvnuAmt: any;
    rcptAmt: any;
    pymRcptAmt: any;
};

type Collection = {
    trgtRvnuAmt: any;
    rcptAmt: any;
    pymRcptAmt: any;
    diffAmt: any;
};

export const TancisPortalMainChart = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();

    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "CLR0032" }, { area: "comnCd", comnCd: "PTL0003" }, { area: "coCd" }],
    });

    const [chartKey, setChartKey] = useState(0);

    const [foundChartTotal, setTotalChart] = useState<Chart>({
        trgtRvnuAmt: "0",
        rcptAmt: "0",
        pymRcptAmt: "0",
    });

    const [colInfo, setColInfo] = useState<Collection>({
        trgtRvnuAmt: "0",
        rcptAmt: "0",
        pymRcptAmt: "0",
        diffAmt: "0",
    });

    const handleError = (error: any) => {
        const content = error.response?.data?.errorMessage || "error";
        showToast({ content, type: "error" });
    };

    const yearOptions = Array.from({ length: new Date().getFullYear() - 2005 + 1 }, (_, i) => {
        const year = new Date().getFullYear() - i;
        return {
            label: `${year}`,
            value: `${year}`,
        };
    });

    const monthKeyMap: Record<string, string> = {
        "01": "L_JAN",
        "02": "L_FEB",
        "03": "L_MAR",
        "04": "L_APR",
        "05": "L_MAY",
        "06": "L_JUN",
        "07": "L_JUL",
        "08": "L_AUG",
        "09": "L_SEP",
        "10": "L_OCT",
        "11": "L_NOV",
        "12": "L_DEC",
    };

    const fiscalMonths = ["07", "08", "09", "10", "11", "12", "01", "02", "03", "04", "05", "06"];

    const monthOptions = fiscalMonths.map((month) => ({
        label: t(monthKeyMap[month] ?? month),
        value: month,
    }));

    const SF_SRCH: TFormSchema = {
        id: "form_mainSrch",
        schema: {
            srchCtraYy: {
                type: "select",
                required: true,
            },
            srchTaxTp: {
                type: "select",
                // label: "L_TAX_TP",
                // area: "comnCd",
                // comnCd: "CLR0032",
                options: [
                    { label: "Import Duty", value: "IMP" },
                    { label: "Export Duty", value: "EXP" },
                    { label: "Excise Duty For Aged Vehicles", value: "EXA" },
                    { label: "Trade Levy Zanzibar", value: "LEV" },
                    { label: "Excise Duty", value: "EX" },
                    { label: "Fuel Levy", value: "FLV" },
                    { label: "Petroleum Levy", value: "PLV" },
                    { label: "Vehicle Registration Tax", value: "VEH" },
                    { label: "Customs Processing Fee", value: "CPF" },
                    { label: "Railway Development Levy", value: "RDL" },
                    { label: "Import Motor Vehicle Fee", value: "MEV" },
                    { label: "Transfer Motor Vehicle Fee", value: "VEM" },
                    { label: "Copyright Levy", value: "CLV" },
                    { label: "Infrastructure Tax", value: "IST" },
                    { label: "VAT", value: "VAT" },
                    { label: "EXPORT VAT", value: "EVA" },
                    { label: "Export Levy", value: "ELV" },
                    { label: "HRL", value: "HRL" },
                    { label: "Penalty/Fine", value: "_PF" },
                    { label: "Service Fee", value: "_SF" },
                    { label: "Others", value: "_OT" },
                ],
                all: true,
            },
            srchSctnCd: {
                type: "select",
                // label: "L_REGN",
                all: true,
            },
            srchYy: {
                type: "select",
                options: yearOptions,
                required: true,
            },
            srchMm: {
                type: "select",
                // label: "L_REFF_MM",
                options: monthOptions,
                all: true,
            },
            srchMnldYn: {
                type: "select",
                options: [
                    { label: "Mainland", value: "Y" },
                    { label: "Zanzibar", value: "N" },
                ],
                defaultValue: "Y",
            },
        },
    };
    const form = {
        mainSrch: useForm({
            defaultSchema: SF_SRCH,
            defaultValues: {
                srchMm: String(new Date().getMonth() + 1).padStart(2, "0"),
            },
        }),
    };

    const fiscalYyMm = (srchCtraYy: any, srchMm: any) => {
        const ctra = String(srchCtraYy ?? "");
        const mm = String(srchMm ?? "").padStart(2, "0");

        const [startY, endY] = ctra.split("/");
        if (!startY || !endY || !mm) return "";

        const year = Number(mm) >= 7 ? startY : endY;
        return `${year}${mm}`;
    };

    const findChartTotal = async () => {
        try {
            const base = process.env.REACT_APP_API_PTLI ?? "";
            const path = "/api/v1/ptl/comn/comn/main/chart-total";

            const srchCtraYy = form.mainSrch.getValue("srchCtraYy");
            const srchTaxTp = form.mainSrch.getValue("srchTaxTp");
            const srchSctnCd = form.mainSrch.getValue("srchSctnCd");
            const srchMm = form.mainSrch.getValue("srchMm");
            const srchMnldYn = form.mainSrch.getValue("srchMnldYn");

            const srchYyMm = fiscalYyMm(srchCtraYy, srchMm);
            const srchYy = srchYyMm ? srchYyMm.slice(0, 4) : "";

            const params = new URLSearchParams({
                srchCtraYy: String(srchCtraYy ?? ""),
                srchTaxTp: String(srchTaxTp ?? ""),
                srchSctnCd: String(srchSctnCd ?? ""),
                srchYy: String(srchYy ?? ""),
                srchMm: String(srchMm ?? ""),
                srchYyMm: String(srchYyMm ?? ""),
                srchMnldYn: String(srchMnldYn ?? ""),
            });

            const url = `${base}${path}?${params.toString()}`;

            const response = await api.get(url);
            const content = response.data.chartTotal.content;

            setTotalChart(content);
            setColInfo(content);
        } catch (error) {
            handleError(error);
        }
    };

    const tempTaxTypeOptions = [
        { label: "Penalty/Fine", value: "_PF" },
        { label: "Service Fee", value: "_SF" },
        { label: "Others", value: "_OT" },
    ];

    const appendOptionsKeepBase = (base: any[] = [], extra: any[] = []) => {
        const seen = new Set(base.map((o) => String(o?.value)));
        const appended = [...base];

        extra.forEach((o) => {
            const key = String(o?.value);
            if (!key) return;
            if (seen.has(key)) return;
            seen.add(key);
            appended.push(o);
        });

        return appended;
    };

    const handler = {
        setInit: async () => {
            handler.setCtraYy();
            handler.setSctn();

            findChartTotal();
        },
        setCtraYy: async () => {
            const today = dayjs(comnUtils.getDate());
            const currentYear = today.year();
            const currentMonth = today.month() + 1;
            // 회계연도: 7월 시작(7~6)
            const FISCAL_START_MONTH = 7;
            const LIST_START_YEAR = 2020;

            // 7월 이전(1~6)이면 전년도 회계연도, 7월 이상이면 당해 회계연도
            const fiscalStartYear = currentMonth < FISCAL_START_MONTH ? currentYear - 1 : currentYear;

            const currentValue = `${fiscalStartYear}/${fiscalStartYear + 1}`;

            const list: string[] = [];
            // 회계연도 목록 생성 (2020/2021 ~ 현재)
            for (let i = LIST_START_YEAR; i <= currentYear; i++) {
                list.push(`${i}/${i + 1}`);
            }

            form.mainSrch.setSchema("srchCtraYy", {
                options: list.map((item) => ({
                    label: item,
                    value: item,
                })),
            });

            form.mainSrch.setValue("srchCtraYy", currentValue);
        },
        setSctn: async () => {
            try {
                const base = process.env.REACT_APP_API_PTLI ?? "";
                const path = "/api/v1/ptl/comn/comn/main/sctn";

                const url = `${base}${path}`;

                const response = await api.get(url);
                const content = response.data.sctnList.content ?? [];

                const options = content.map((item: any) => ({
                    label: item.cstmOrgzNm,
                    value: item.cstmOrgzCd,
                }));

                form.mainSrch.setSchema("srchSctnCd", {
                    options,
                });
            } catch (error) {
                handleError(error);
            }
        },
    };

    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Group>
            <Group.Header>
                <div className="flex flex-wrap">
                    <h2 className="mr-2">{t("T_COLT_TRGT_PFMC")}</h2>
                    <p className="text-sm text-gray">{t("T_COLT_DEPT_YYYY_PFMC")}</p>
                </div>
            </Group.Header>
            <Group.Body>
                <Group.Section>
                    <div
                        className={[
                            "rounded-md border p-4",
                            "border-gray-200",
                            "dark:bg-gray-900 dark:border-gray-700",
                        ].join(" ")}
                    >
                        <div className="grid grid-cols-10 gap-4">
                            {/* Criteria Year */}
                            <div className="col-span-2 md:col-span-4">
                                <div className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {t("L_CTRA_YY")}
                                </div>
                                <Group.Control
                                    {...form.mainSrch.schema.srchCtraYy}
                                    onChange={() => {
                                        findChartTotal();
                                    }}
                                />
                            </div>
                            {/* Month */}
                            <div className="col-span-2 md:col-span-4">
                                <div className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {t("L_REFF_MM")}
                                </div>
                                <Group.Control
                                    {...form.mainSrch.schema.srchMm}
                                    onChange={() => {
                                        findChartTotal();
                                    }}
                                />
                            </div>
                            {/* Mainland / Zanzibar */}
                            <div className="col-span-2 md:col-span-4">
                                <div className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {t("L_MNLD_YN")}
                                </div>
                                <Group.Control
                                    {...form.mainSrch.schema.srchMnldYn}
                                    onChange={() => {
                                        findChartTotal();
                                    }}
                                />
                            </div>
                            {/* Tax Type */}
                            <div className="col-span-2 md:col-span-4">
                                <div className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {t("L_TAX_TP")}
                                </div>
                                <Group.Control
                                    {...form.mainSrch.schema.srchTaxTp}
                                    onChange={() => {
                                        findChartTotal();
                                    }}
                                />
                            </div>
                            {/* Region */}
                            <div className="col-span-2 md:col-span-4">
                                <div className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {t("L_REGN")}
                                </div>
                                <Group.Control
                                    {...form.mainSrch.schema.srchSctnCd}
                                    onChange={() => {
                                        findChartTotal();
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </Group.Section>
                <CollectionInfo colInfo={colInfo} srchMm={form.mainSrch.watch("srchMm")} />
                <CollectionChart foundChartTotal={foundChartTotal} />
            </Group.Body>
        </Group>
    );
};

const normalChartList = (data: any[]) => {
    const list = Array.isArray(data) ? data : [];
    const hasMonthly = list.some((it) => it?.ctraYm); // 월별 데이터 존재 여부

    if (hasMonthly) {
        return [...list].filter((it) => it?.ctraYm).sort((a, b) => Number(a.ctraYm) - Number(b.ctraYm));
    }

    if (list.length === 1) {
        const row = list[0] ?? {};
        return [
            {
                ...row,
                ctraYm: "TOTAL",
                isTotal: true,
            },
        ];
    }

    return [];
};

const pickSummaryRow = (data: any[]) => {
    const list = normalChartList(data);
    if (list.length === 0) return null;

    return list[list.length - 1];
};

const toArray = <T,>(v: unknown): T[] => {
    if (Array.isArray(v)) return v as T[];
    if (v == null) return [];
    // 단일 객체가 올 수도 있으면 1건 배열로 취급
    if (typeof v === "object") return [v as T];
    return [];
};

const toNumber = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const formatTZS = (amount: any, digits = 2) => {
    const n = toNumber(amount);
    return `TZS ${Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })}`;
};

const formatTZSWithSign = (amount: any, digits = 2) => {
    const n = toNumber(amount);
    const sign = n < 0 ? "- " : "";
    return `${sign}${formatTZS(n, digits)}`;
};

const CollectionInfo = (props: any) => {
    const { colInfo, srchMm } = props;
    const { t } = useTranslation();

    // 20260407
    let trgt = 0;
    let rcpt = 0;
    let pym = 0;
    let diff = 0;

    if ("" === srchMm) {
        toArray<any>(colInfo).map((it: any) => {
            trgt += toNumber(it.trgtRvnuAmt ?? 0);
            rcpt += toNumber(it.rcptAmt ?? 0);
            pym += toNumber(it.pymRcptAmt ?? 0);
        });
    } else {
        const row = pickSummaryRow(toArray<any>(colInfo));

        trgt = toNumber(row?.trgtRvnuAmt);
        rcpt = toNumber(row?.rcptAmt);
        pym = toNumber(row?.pymRcptAmt);
    }

    diff = rcpt - trgt;

    const pct = (num: number, den: number) => {
        if (!Number.isFinite(den) || den <= 0) return null; // 기준값 0이면 표시 불가
        return (num / den) * 100;
    };

    const fmtPct = (v: number | null) => (v == null ? "0.00%" : `${v.toFixed(2)}%`);

    const collectionPct = pct(rcpt, trgt);
    const priviousColPct = pct(pym, rcpt);
    const differencePct = pct(diff, trgt);

    return (
        <div className="w-full border rounded">
            <div className="flex gap-4 justify-between">
                {[
                    { label: t("L_COLT_TRGT_RVNU"), value: formatTZSWithSign(trgt), colorClass: "text-[#00ad59]" },
                    {
                        label: "Performance",
                        value: formatTZSWithSign(rcpt),
                        sub: fmtPct(collectionPct),
                        colorClass: "text-[#0479ce]",
                    },
                    // { label: "Previous Year", value: formatTZSWithSign(pym) , sub: fmtPct(priviousColPct), colorClass: "text-[#ffb100]" },
                    {
                        label: "Difference",
                        value: formatTZSWithSign(diff),
                        sub: fmtPct(differencePct),
                        colorClass: diff < 0 ? "text-[#dc2626]" : "text-[#00ad59]",
                    },
                ].map((item) => (
                    <div
                        key={item.label}
                        className="flex-1 min-w-0 shadow rounded-lg p-4 flex flex-col items-center justify-center"
                    >
                        <div className="text-gray-500 text-sm">{item.label}</div>
                        <div className={`text-2xl font-semibold ${item.colorClass}`}>{item.value}</div>
                        <div className={`text-lg ${item.colorClass}`}>{item.sub}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const CollectionChart = (props: any) => {
    const { foundChartTotal } = props;
    const { t } = useTranslation();

    const sortList = normalChartList(foundChartTotal);

    const trgtMax = Math.max(
        0,
        ...sortList.map((it) => Number(it.trgtRvnuAmt ?? 0)),
        ...sortList.map((it) => Number(it.rcptAmt ?? 0)),
        // ...sortList.map((it) => Number(it.pymRcptAmt ?? 0)),
    );

    const yMax = trgtMax * 1.2;
    const axisUnit: "T" | "B" | "M" | "K" | "" =
        yMax >= 1e12 ? "T" : yMax >= 1e9 ? "B" : yMax >= 1e6 ? "M" : yMax >= 1e3 ? "K" : "";

    const unitDiv =
        axisUnit === "T" ? 1e12 : axisUnit === "B" ? 1e9 : axisUnit === "M" ? 1e6 : axisUnit === "K" ? 1e3 : 1;

    const fmtAxis = (v: any) => {
        const n = Number(v ?? 0);
        if (!Number.isFinite(n)) return "0";
        if (axisUnit === "") return `${n.toFixed(0)}`;
        return `${(n / unitDiv).toFixed(2)}${axisUnit}`;
    };

    return (
        <div className="w-full border rounded">
            <ReactECharts
                notMerge={true}
                lazyUpdate={false}
                option={{
                    grid: {
                        left: "120px",
                        right: "30px",
                        top: "30px",
                        bottom: "30px",
                    },
                    xAxis: {
                        type: "category",
                        // data: ["Previous Year", "Performance", "Revenue"], // X축 레이블을 데이터 순서(월)에 맞게 설정
                        data: sortList.map((item) => item.ctraYm),
                        axisLabel: {
                            fontSize: 10,
                            formatter: (value: string) => {
                                if (value === "TOTAL") return t("L_TOT") ?? "Total";
                                const mm = value?.slice(-2);
                                const map: Record<string, string> = {
                                    "01": t("L_JAN"),
                                    "02": t("L_FEB"),
                                    "03": t("L_MAR"),
                                    "04": t("L_APR"),
                                    "05": t("L_MAY"),
                                    "06": t("L_JUN"),
                                    "07": t("L_JUL"),
                                    "08": t("L_AUG"),
                                    "09": t("L_SEP"),
                                    "10": t("L_OCT"),
                                    "11": t("L_NOV"),
                                    "12": t("L_DEC"),
                                };
                                return map[mm] ?? mm;
                            },
                        },
                    },
                    yAxis: {
                        type: "value",
                        max: yMax, // 최대값을 120%로 설정하여 100%를 초과하는 값을 표시
                        interval: yMax * 0.1, // 10% 단위로 나누기
                        axisLabel: {
                            fontSize: 10,
                            formatter: (v: any) => fmtAxis(v),
                        },
                        splitLine: {
                            show: true,
                            opacity: 0.5,
                        },
                    },
                    tooltip: {
                        trigger: "axis",
                        axisPointer: { type: "shadow" },
                        formatter: (params: any) => {
                            const ym = params?.[0]?.axisValue ?? "";
                            const row = sortList.find((it) => String(it.ctraYm) === String(ym)) ?? {};
                            const trgt = Number(row.trgtRvnuAmt ?? 0);
                            // 월 제목(필요하면 월 라벨 치환)
                            const mm = String(ym).slice(-2);
                            const monthMap: Record<string, string> = {
                                "01": t("L_JAN"),
                                "02": t("L_FEB"),
                                "03": t("L_MAR"),
                                "04": t("L_APR"),
                                "05": t("L_MAY"),
                                "06": t("L_JUN"),
                                "07": t("L_JUL"),
                                "08": t("L_AUG"),
                                "09": t("L_SEP"),
                                "10": t("L_OCT"),
                                "11": t("L_NOV"),
                                "12": t("L_DEC"),
                            };
                            const title = monthMap[mm] ?? ym;

                            const lines = params
                                .filter((p: any) => p.seriesName !== (t("L_TAX_AMT") as any))
                                .map((p: any) => {
                                    const value = Number(p.value ?? 0); // 막대 값(금액)
                                    const percentage = trgt > 0 ? (value / trgt) * 100 : 0; // 월별 목표 대비 %

                                    return `${p.marker} ${
                                        p.seriesName
                                    }: ${value.toLocaleString()} (${percentage.toFixed(2)}%)`;
                                });
                            return [title, ...lines].join("<br/>");
                        },
                    },
                    series: [
                        {
                            name: t("L_COLT_TRGT_RVNU"),
                            type: "bar",
                            data: sortList.map((item) => Number(item.trgtRvnuAmt ?? 0)),
                            itemStyle: { color: "#00ad59" },
                            barMaxWidth: 28,
                        },
                        {
                            name: "Performance", //t("T_COLT_TRGT_PFMC"),
                            type: "bar",
                            data: sortList.map((item) => Number(item.rcptAmt ?? 0)),
                            itemStyle: { color: "#0479ce" },
                            barMaxWidth: 28,
                        },
                        // {
                        //     name: t("L_TAX_AMT"),
                        //     type: "bar",
                        //     data: sortList.map((item) => Number(item.pymRcptAmt ?? 0)),
                        //     itemStyle: { color: "#ffb100" },
                        //     barMaxWidth: 28,
                        // },
                    ],
                }}
            />
        </div>
    );
};
